import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Drawer from '@material-ui/core/Drawer';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Form from 'components/form';
import Button from 'components/common/Button';
import TextField from 'components/form/Input';
import Select from 'components/form/Select';
import Dropzone from 'components/form/Dropzone';
import DateInput from 'components/form/DateInput';
import CheckBox from 'components/form/Checkbox';
import useSnack from 'hooks/useSnack';
import Toggle from '@material-ui/core/Switch';
import { FormControlLabel } from '@material-ui/core';
import { GET_ALL_PRODUCTS } from 'graphql/products';
import { GET_USERS_WITHOUT_SUB, ADD_SUB } from 'graphql/admin/subscriptions';
import { UPDATE_STUDENT_PROFILE } from 'graphql/admin/students';

const { FormItem } = Form;

const useStyles = makeStyles(() => ({
  drawer: {
    width: 600,
    padding: 20,
  },
  close: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
}));

const AddSubscription = ({ history, form }) => {
  const classes = useStyles();
  const snack = useSnack();
  const client = useApolloClient();
  const user = useSelector((s) => s.user.user);
  const productsResp = useQuery(GET_ALL_PRODUCTS);
  const [addSubMutation] = useMutation(ADD_SUB);
  const [updateProfileMutation] = useMutation(UPDATE_STUDENT_PROFILE);
  const { getFieldDecorator, validateFields, getFieldValue } = form;
  const onClose = () => history.push('/subscriptions');
  const product = getFieldValue('product');

  const onSave = () => {
    return validateFields().then(async (values) => {
      try {
        await addSubMutation({
          variables: {
            data: {
              student_id: values.student_id.value,
              plan_id: product.plan_id,
              total: values.total,
              installments: values.installments,
              installment_amount: values.installment_amount,
              tier_prices: '',
              signup_fee: values.signup_fee,
              start_date: values.start_date,
              end_date: values.end_date,
              created_by: user.id,
              status: 'active',
              contract_filename: product.contract_filename,
            },
          },
        });
        await updateProfileMutation({
          variables: {
            userId: values.student_id.value,
            data: {
              product_plan_id: product.plan_id,
              product_type: product.product_type,
              is_trial: false,
            },
          },
        });
        snack.success('Subscription added');
        onClose();
      } catch (e) {
        snack.error(e.message);
      }
    });
  };

  return (
    <Drawer
      open
      onClose={onClose}
      disableBackdropClick
      anchor="right"
      classes={{ paper: classes.drawer }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={3}
      >
        <Typography variant="h5">Add Subscription</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box>
        <Form>
          <FormItem>
            {getFieldDecorator('student_id', {
              rules: [{ required: true }],
            })(
              <Select
                async
                debounceTimeout={300}
                cacheOptions
                defaultOptions
                placeholder="Search..."
                loadOptions={(query) => {
                  return client
                    .query({
                      query: GET_USERS_WITHOUT_SUB,
                      variables: { query: query ? `%${query}%` : undefined },
                    })
                    .then(({ data }) => {
                      return data.v_students
                        .filter((i) => !i.has_active_subscription)
                        .map((i) => ({
                          value: i.user_id,
                          label: `${i.user_first_name} ${i.user_last_name} (${i.user_email})`,
                        }));
                    });
                }}
                isMulti={false}
                label="Select Student"
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('product', {
              rules: [{ required: true }],
            })(
              <Select
                isMulti={false}
                label="Product"
                options={
                  productsResp.data
                    ? productsResp.data.products.map((i) => ({
                        ...i,
                        label: `${i.package_name} - ${i.graduation_year} - ${i.pricing_model}`,
                        value: i.plan_id,
                      }))
                    : []
                }
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('total', {
              rules: [{ required: true }],
            })(<TextField type="number" label="Total Price" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator('signup_fee', {
              initialValue: 0,
            })(
              <TextField
                label="Signup fee"
                hint="Enter 0 if no fee"
                type="number"
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator(
              'installments',
              {},
            )(<TextField type="number" label="Number of installments" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator(
              'installment_amount',
              {},
            )(<TextField type="number" label="Installment amount" />)}
          </FormItem>
          <FormItem>
            {getFieldDecorator('start_date', {
              rules: [{ required: true }],
            })(
              <DateInput
                disablePast
                format="MM-DD-YYYY"
                label="Program start date"
                inputVariant="outlined"
                fullWidth
                margin="dense"
              />,
            )}
          </FormItem>
          <FormItem>
            {getFieldDecorator('end_date', {
              rules: [{ required: true }],
            })(
              <DateInput
                disablePast
                format="MM-DD-YYYY"
                label="Program end date"
                inputVariant="outlined"
                fullWidth
                margin="dense"
              />,
            )}
          </FormItem>
        </Form>
        <br />
        <Button onClick={onSave} variant="contained" color="primary">
          Submit
        </Button>
      </Box>
    </Drawer>
  );
};

export default Form.create()(AddSubscription);
